import type { SettingsTarget } from "@/components/settings/settings-navigation";
import { useCallback } from "react";
import type { SwitcherProject } from "@/components/panels/space-switcher";
import { ProjectContextMenuContent } from "@/components/panels/project-context-menu-content";
import { useProjectStore } from "@/stores/project-store";

interface CompactSwitcherContextMenuArgs {
  openSettings: (target: SettingsTarget) => void;
}

export function useCompactSwitcherContextMenuRenderers({
  openSettings,
}: CompactSwitcherContextMenuArgs) {
  const renameProject = useProjectStore((s) => s.renameProject);
  const updateProjectColor = useProjectStore((s) => s.updateProjectColor);
  const toggleProjectPinned = useProjectStore((s) => s.toggleProjectPinned);
  const deleteProject = useProjectStore((s) => s.deleteProject);

  const handleProjectSettings = useCallback(
    (project: SwitcherProject) => {
      openSettings({
        page: "projects-project",
        args: { projectId: project.id, title: project.name },
      });
    },
    [openSettings],
  );

  const renderProjectContextMenu = useCallback(
    (project: SwitcherProject) => (
      <ProjectContextMenuContent
        project={project}
        onRename={(name) => void renameProject(project.id, name)}
        onSetColor={(projectId, color) =>
          void updateProjectColor(projectId, color)
        }
        onPin={(projectId) => void toggleProjectPinned(projectId)}
        onSettings={() => handleProjectSettings(project)}
        onDelete={(projectId) => void deleteProject(projectId)}
      />
    ),
    [
      deleteProject,
      handleProjectSettings,
      renameProject,
      toggleProjectPinned,
      updateProjectColor,
    ],
  );

  return { renderProjectContextMenu };
}
